/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
'use memo';
import { useLabels } from '@/hooks/useLabels';
import azCommonStyles from '@/theme/globals.module.scss';
import cx from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Skeleton from '@/components/Skeleton/Skeleton';
import styles from './styles.module.scss';
import { MAX_VISIBLE_THUMBNAILS } from '@/components/Carousel/constants';
import type { CarouselItem, CarouselItemTypes, ThumbnailProps } from './types';
import { MemoizedThumbnail } from './MemoizedThumbnail';

const labelMap = {
  seeMoreLabel: 'hyperlink_label_shelf_See_More',
  imagesHeader: 'label_Thumbnails_header_Images',
  videoHeader: 'label_Thumbnails_header_Videos',
};

const ThumbnailComponentV2 = (props: ThumbnailProps) => {
  const {
    classes = {},
    handleSeeMoreClick,
    handleThumbnailClick,
    groupedLayout,
    selectedThumbnailIndex,
    snapToThumbnails,
    hoverEnabled,
    loading,
    onImageChange,
    items = [],
    imgTitle,
    maxNumberOfThumbnails,
    enableScroll = false,
    forceShowSeeMore = false,
  } = props;

  const { seeMoreLabel, imagesHeader, videoHeader } = useLabels(labelMap);
  const numberOfThumbnailsToDisplay = maxNumberOfThumbnails ?? MAX_VISIBLE_THUMBNAILS;
  const thumbnailListRef = useRef({});
  const thumbnailRefs = useRef({});
  const justMounted = useRef(true);
  const [selectedThumbnail, setSelectedThumbnail] = useState(0);
  const { thumbnailGroup: thumbnailGroupClass = '', thumbnailList: thumbnailListClass } = classes;
  const thumbnails = items;

  const getThumbnailsByType = (type: CarouselItemTypes): CarouselItem[] => {
    return thumbnails.filter((thumbnail: CarouselItem) => thumbnail.type === type);
  };

  const imageThumbnails = getThumbnailsByType('image');
  const getTrimmedThumbnails: () => CarouselItem[] = () => {
    return thumbnails.slice(0, numberOfThumbnailsToDisplay);
  };

  const generateAccessibleLabel = (
    thumbnailItem: CarouselItem,
    thumbIndex: number,
    isSeeMore = false,
  ): string => {
    if (isSeeMore) {
      return seeMoreLabel;
    }

    const { type, alt, title } = thumbnailItem;
    const productName = imgTitle || title || alt || 'product';
    const position = thumbIndex + 1;

    switch (type) {
      case 'video':
        return `Play ${productName} video ${position}`;
      case 'spin360':
        return `View ${productName} 360-degree image ${position}`;
      case 'image':
      default:
        return `View ${productName} image ${position}`;
    }
  };

  const showExpandedImg = useCallback(
    (carouselItem: CarouselItem) => {
      const { enlargedMediaUrl } = carouselItem;
      const index = thumbnails.findIndex((img) => img.enlargedMediaUrl === enlargedMediaUrl);

      if (index < 0) {
        return;
      }

      if (onImageChange) {
        onImageChange(index, carouselItem);
      }

      setSelectedThumbnail(index);
    },
    [thumbnails, onImageChange],
  );

  const handleClick = useCallback(
    (thumbnailItem: CarouselItem) => {
      showExpandedImg(thumbnailItem);
      handleThumbnailClick?.(thumbnailItem);
    },
    [showExpandedImg, handleThumbnailClick],
  );

  useEffect(() => {
    if (
      !!thumbnailRefs &&
      !!thumbnailListRef &&
      snapToThumbnails &&
      selectedThumbnailIndex !== undefined
    ) {
      // @ts-expect-error fix type
      const thumbnailRef = thumbnailRefs.current[selectedThumbnailIndex] || {};
      const listRef: { scroll?: (scrollObj: unknown) => void } = thumbnailListRef.current || {};
      const thumbnailOffsetLeft = thumbnailRef.offsetLeft;
      const thumbnailWidth = thumbnailRef.offsetWidth;
      // @ts-expect-error fix type
      const thumbnailListCenter = listRef.offsetWidth / 2;
      const thumbnailFromLeftDistance = thumbnailOffsetLeft + thumbnailWidth / 2;
      const toCenter = thumbnailFromLeftDistance - thumbnailListCenter;
      const scrollBehavior = justMounted.current ? 'auto' : 'smooth';
      if (listRef.scroll) {
        listRef.scroll({
          left: toCenter,
          top: 0,
          behavior: scrollBehavior,
        });
      }
      justMounted.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedThumbnailIndex]);
  const thumbnailStyle = `${styles.thumbnailBox} ${styles.inlineThumbnailBox}`;
  const thumbnailContainerStyle = `${styles.thumbnailsContainer} ${styles.thumbnailsContainerHorizontal}`;

  const renderThumbnail = (thumbnailItem: CarouselItem, thumbIndex: number) => {
    const { type } = thumbnailItem;
    const isVideo = type === 'video';
    const isLastVisible = thumbIndex + 1 === numberOfThumbnailsToDisplay;
    const isSeeMoreButton = isLastVisible && forceShowSeeMore;
    const actualThumbnailIndex =
      isVideo && groupedLayout ? thumbIndex + imageThumbnails.length : thumbIndex;

    const isSelected =
      !isSeeMoreButton &&
      (selectedThumbnailIndex !== undefined
        ? selectedThumbnailIndex === actualThumbnailIndex
        : selectedThumbnail === actualThumbnailIndex);

    const onMouseEnter =
      hoverEnabled && !isSeeMoreButton
        ? () => {
            showExpandedImg(thumbnailItem);
          }
        : () => {};

    const onClick = isSeeMoreButton
      ? () => handleSeeMoreClick?.()
      : () => handleClick(thumbnailItem);

    return (
      <MemoizedThumbnail
        thumbnailItem={thumbnailItem}
        thumbIndex={thumbIndex}
        isSelected={isSelected}
        isSeeMoreButton={isSeeMoreButton}
        ariaLabel={generateAccessibleLabel(thumbnailItem, thumbIndex, isSeeMoreButton)}
        onMouseEnter={onMouseEnter}
        onClick={onClick}
        thumbnailRef={(element) => {
          // @ts-expect-error fix type
          thumbnailRefs.current[actualThumbnailIndex] = element;
        }}
      />
    );
  };

  const renderThumbnailList = (thumbnails: CarouselItem[]) => {
    return (
      <ul
        // @ts-expect-error fix type
        ref={thumbnailListRef}
        className={cx(thumbnailContainerStyle, thumbnailListClass, {
          [styles.noScroll]: !enableScroll,
          [classes.thumbnailContainer || '']: classes.thumbnailContainer,
        })}
        data-testid="thumbnails-container"
      >
        {!loading && thumbnails?.map(renderThumbnail)}
        {loading &&
          thumbnails?.map((thumbnail, index) => (
            <li key={index} className={thumbnailStyle}>
              <Skeleton
                animation="wave"
                height={70}
                width={70}
                variant="rect"
                data-testid="skeleton-loader"
              />
            </li>
          ))}
      </ul>
    );
  };

  const renderThumbnailGroup = (thumbnails: CarouselItem[] = [], header: string) => {
    return (
      <div className={styles.thumbnailGroup}>
        {!!header && !!thumbnails.length && (
          <div className={cx(styles.thumbnailHeading, azCommonStyles['az-title-5-medium'])}>
            {header}
          </div>
        )}
        {renderThumbnailList(thumbnails)}
      </div>
    );
  };

  if (groupedLayout) {
    const videoThumbnails = getThumbnailsByType('video');
    return (
      <div className={thumbnailGroupClass}>
        {renderThumbnailGroup(imageThumbnails, imagesHeader)}
        {renderThumbnailGroup(videoThumbnails, videoHeader)}
      </div>
    );
  }

  return renderThumbnailList(getTrimmedThumbnails());
};

export default ThumbnailComponentV2;
